import type { FormGroup } from '@angular/forms';
import type { Form, FormField } from '../interfaces/form.interface';
import type {
    ReactiveFieldSerializerDefinition,
    ReactiveFieldSerializerOmitContext,
    ReactiveFormPayloadSerializerResolvers,
    ReactiveFormSerializerDefinition,
    ReactiveRepeatableSerializerTarget,
    ReactiveRepeatableSerializerTargetInput,
} from '../interfaces/reactive-form-serializer.interface';
import type {
    RepeatableGroupConfiguration,
    RepeatableLegacyAggregateTarget,
    RepeatableLegacyAggregateTargetInput,
    RepeatableLegacySlotTarget,
    RepeatableLegacySlotTargetInput,
    RepeatableSubField,
} from '../interfaces/input.interface';
import { InputType } from '../interfaces/input.interface';
import { RepeatableValueUtils } from '../field/repeatable-group/logic/repeatable-value.utils';
import { ReactiveFormValueUtils } from '../model/reactive-form-value.utils';
import { FormOperations } from './form-operations';

const TIME_PAD_LENGTH = 2;
const DATE_PAD_LENGTH = 2;
const CATALOG_KEY_SEPARATOR = ':';
const SLOT_INDEX_PLACEHOLDER = '{index}';
const SLOT_NUMBER_PLACEHOLDER = '{n}';
const BOOLEAN_TRUE_STRING_VALUES = ['1', 'true', 'si', 'sí', 's', 'yes'];
const BOOLEAN_FALSE_STRING_VALUES = ['0', 'false', 'no', 'n'];

export class ReactiveFormPayloadSerializer {
    public static serialize<T>(
        form: Form<T>,
        data: Record<string, unknown>,
        resolvers: ReactiveFormPayloadSerializerResolvers = {},
    ): Record<string, unknown> {
        const fieldMap = this.createSerializableFieldMap(form);
        const payload = Object.entries(data).reduce((result, [key, value]) => {
            if (!key.trim()) {
                return result;
            }

            const field = fieldMap.get(key);

            if (!field || field.input.type === InputType.REPEATABLE_GROUP) {
                return result;
            }

            const primaryKey = ReactiveFormValueUtils.getFieldBackendValueKey(field);
            const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);

            if (Array.isArray(value)) {
                result[key] = value
                    .map((item) => this.extractSerializableValue(item, primaryKey, identityKeys))
                    .filter((item) => item !== null && item !== undefined && item !== '')
                    .join(';');

                return result;
            }

            if (value instanceof Date && this.isTimeField(field, key)) {
                result[key] = this.formatTimeValue(value);
                return result;
            }

            if (value instanceof Date && this.isDateField(field)) {
                result[key] = this.formatDateValue(value, this.resolveDateFieldFormat(field));
                return result;
            }

            result[key] = this.extractSerializableValue(value, primaryKey, identityKeys);

            return result;
        }, {} as Record<string, unknown>);

        const configuredPayload = this.applySerializerDefinition(form.serializer, data, payload, fieldMap, resolvers);
        const aggregatePayload = this.applyRepeatableLegacyAggregateSerializations(form, data, configuredPayload);
        const slotPayload = this.applyRepeatableLegacySlotSerializations(form, data, aggregatePayload);

        return this.removeConfiguredFields(form.serializer, slotPayload);
    }

    public static serializeRepeatableLegacyFields<T>(
        form: Form<T>,
        data: Record<string, unknown>,
        resolvers: ReactiveFormPayloadSerializerResolvers = {},
    ): Record<string, unknown> {
        const fieldMap = this.createSerializableFieldMap(form);
        const payload: Record<string, unknown> = {};
        const configuredPayload = this.applySerializerDefinition(form.serializer, data, payload, fieldMap, resolvers);
        const aggregatePayload = this.applyRepeatableLegacyAggregateSerializations(form, data, configuredPayload);
        const slotPayload = this.applyRepeatableLegacySlotSerializations(form, data, aggregatePayload);

        return this.removeConfiguredFields(form.serializer, slotPayload);
    }

    private static createSerializableFieldMap<T>(form: Form<T>): Map<string, FormField<T>> {
        const fieldMap = new Map<string, FormField<T>>();

        for (const section of form.sections ?? []) {
            for (const field of section.fields ?? []) {
                if (!this.isSerializableField(section.isVisible, field)) {
                    continue;
                }

                this.setSerializableField(fieldMap, field.name, field);

                if (field.input.type === InputType.TWO_FIELDS_CONCAT) {
                    this.setSerializableField(fieldMap, field.input.twoFieldsConcatConfiguration?.subField1, field);
                    this.setSerializableField(fieldMap, field.input.twoFieldsConcatConfiguration?.subField2, field);
                }
            }
        }

        return fieldMap;
    }

    private static isSerializableField<T>(sectionVisible: boolean, field: FormField<T>): boolean {
        if (!field.name?.trim()) {
            return false;
        }

        if (!sectionVisible || !field.conditions.isVisible) {
            return false;
        }

        return field.input.type !== InputType.EMPTY && field.input.type !== InputType.EXTERNAL;
    }

    private static setSerializableField<T>(
        fieldMap: Map<string, FormField<T>>,
        fieldName: string | undefined,
        field: FormField<T>,
    ): void {
        const normalizedFieldName = String(fieldName ?? '').trim();

        if (!normalizedFieldName) {
            return;
        }

        const existingField = fieldMap.get(normalizedFieldName);

        if (existingField?.input.type === InputType.REPEATABLE_GROUP) {
            return;
        }

        fieldMap.set(normalizedFieldName, field);
    }


    private static applySerializerDefinition<T>(
        serializer: ReactiveFormSerializerDefinition | undefined,
        data: Record<string, unknown>,
        payload: Record<string, unknown>,
        fieldMap: Map<string, FormField<T>>,
        resolvers: ReactiveFormPayloadSerializerResolvers,
    ): Record<string, unknown> {
        if (!serializer?.fields) {
            return payload;
        }

        const nextPayload = { ...payload };

        for (const [fieldName, configuration] of Object.entries(serializer.fields)) {
            const sourceName = configuration.source ?? fieldName;
            const targetName = configuration.target ?? sourceName;
            const field = fieldMap.get(sourceName);
            const rawValue = sourceName in data ? data[sourceName] : nextPayload[sourceName];

            if (!field && sourceName in data) {
                delete nextPayload[targetName];
                delete nextPayload[sourceName];
                continue;
            }

            if (configuration.mode === 'repeatableAggregate') {
                Object.assign(nextPayload, this.serializeConfiguredRepeatableAggregate(rawValue, data, configuration));

                if (configuration.removeSource !== false) {
                    delete nextPayload[sourceName];
                }

                continue;
            }

            if (configuration.mode === 'repeatableSlots') {
                Object.assign(nextPayload, this.serializeConfiguredRepeatableSlots(rawValue, data, configuration));

                if (configuration.removeSource !== false) {
                    delete nextPayload[sourceName];
                }

                continue;
            }

            const normalizedRawValue = configuration.mode === 'repeatableJson'
                ? this.serializeConfiguredRepeatableJson(rawValue, data, configuration)
                : rawValue;
            const serializedValue = this.serializeConfiguredValue(normalizedRawValue, configuration, field, sourceName, resolvers);

            if (this.shouldOmitSerializedValue(configuration, {
                source: sourceName,
                target: targetName,
                value: rawValue,
                serializedValue,
                data,
                payload: nextPayload,
            })) {
                delete nextPayload[targetName];
            } else {
                nextPayload[targetName] = this.hasSerializableValue(serializedValue)
                    ? serializedValue
                    : this.resolveEmptySerializedValue(configuration, serializedValue);
            }

            if (targetName !== sourceName && configuration.removeSource !== false) {
                delete nextPayload[sourceName];
            }
        }

        return nextPayload;
    }

    private static removeConfiguredFields(
        serializer: ReactiveFormSerializerDefinition | undefined,
        payload: Record<string, unknown>,
    ): Record<string, unknown> {
        if (!serializer?.removeFields?.length) {
            return payload;
        }

        const nextPayload = { ...payload };

        for (const fieldName of serializer.removeFields) {
            delete nextPayload[fieldName];
        }

        return nextPayload;
    }

    private static shouldOmitSerializedValue(
        configuration: ReactiveFieldSerializerDefinition,
        context: ReactiveFieldSerializerOmitContext,
    ): boolean {
        if (configuration.omitEmpty === true && !this.hasSerializableValue(context.serializedValue)) {
            return true;
        }

        if (configuration.omitWhen === undefined) {
            return false;
        }

        if (configuration.omitWhen === 'empty') {
            return !this.hasSerializableValue(context.serializedValue);
        }

        if (configuration.omitWhen === 'nullish') {
            return context.serializedValue === null || context.serializedValue === undefined;
        }

        return configuration.omitWhen(context);
    }

    private static resolveEmptySerializedValue(
        configuration: ReactiveFieldSerializerDefinition,
        fallbackValue: unknown,
    ): unknown {
        return Object.prototype.hasOwnProperty.call(configuration, 'emptyValue')
            ? configuration.emptyValue
            : fallbackValue;
    }

    private static serializeConfiguredValue<T>(
        value: unknown,
        configuration: ReactiveFieldSerializerDefinition,
        field: FormField<T> | undefined,
        fieldName: string,
        resolvers: ReactiveFormPayloadSerializerResolvers,
    ): unknown {
        if (!this.hasSerializableValue(value)) {
            return configuration.mode === 'nullableEmpty'
                ? null
                : this.resolveEmptySerializedValue(configuration, value);
        }

        switch (configuration.mode ?? 'value') {
            case 'join':
                return this.serializeJoinedValue(value, configuration, field);
            case 'boolean':
                return this.serializeBooleanValue(value, configuration);
            case 'date':
                return this.serializeDateValue(value, configuration.dateFormat ?? 'iso');
            case 'time':
                return value instanceof Date ? this.formatTimeValue(value) : value;
            case 'catalogKey':
                return this.serializeCatalogKeyValue(value, configuration, field);
            case 'catalogBackendValue':
                return this.serializeCatalogBackendValue(value, configuration, field, fieldName, resolvers);
            case 'optionRawValue':
                return this.serializeOptionRawValue(value, configuration, field, fieldName, resolvers);
            case 'nullableEmpty':
            case 'value':
            default:
                return this.serializeScalarValue(value, configuration, field);
        }
    }

    private static serializeJoinedValue<T>(
        value: unknown,
        configuration: ReactiveFieldSerializerDefinition,
        field: FormField<T> | undefined,
    ): string {
        const values = Array.isArray(value) ? value : [value];
        const separator = configuration.separator ?? ';';

        return values
            .map((item) => this.serializeScalarValue(item, configuration, field))
            .filter((item) => this.hasSerializableValue(item))
            .join(separator);
    }

    private static serializeBooleanValue(value: unknown, configuration: ReactiveFieldSerializerDefinition): unknown {
        const booleanValue = this.coerceBooleanLikeValue(value);

        if (booleanValue === null) {
            return value;
        }

        return booleanValue ? configuration.trueValue ?? 1 : configuration.falseValue ?? 0;
    }

    private static coerceBooleanLikeValue(value: unknown): boolean | null {
        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'number') {
            if (value === 1) {
                return true;
            }

            if (value === 0) {
                return false;
            }
        }

        if (typeof value === 'string') {
            const normalizedValue = value.trim().toLowerCase();

            if (BOOLEAN_TRUE_STRING_VALUES.includes(normalizedValue)) {
                return true;
            }

            if (BOOLEAN_FALSE_STRING_VALUES.includes(normalizedValue)) {
                return false;
            }
        }

        return null;
    }

    private static serializeDateValue(
        value: unknown,
        format: ReactiveFieldSerializerDefinition['dateFormat'],
    ): unknown {
        if (value instanceof Date) {
            return this.formatDateValue(value, format);
        }

        return this.normalizeDateTextValue(value, format);
    }

    private static formatDateValue(
        value: Date,
        format: ReactiveFieldSerializerDefinition['dateFormat'],
    ): string {
        const year = value.getFullYear().toString();
        const month = String(value.getMonth() + 1).padStart(DATE_PAD_LENGTH, '0');
        const day = String(value.getDate()).padStart(DATE_PAD_LENGTH, '0');

        return format === 'spanish' ? `${day}/${month}/${year}` : `${year}-${month}-${day}`;
    }

    private static normalizeDateTextValue(
        value: unknown,
        format: ReactiveFieldSerializerDefinition['dateFormat'],
    ): unknown {
        if (!this.hasSerializableValue(value) || format !== 'spanish') {
            return value;
        }

        const textValue = String(value).trim();
        const isoMatch = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(textValue);

        if (isoMatch !== null) {
            const year = isoMatch[1] ?? '';
            const month = isoMatch[2] ?? '';
            const day = isoMatch[3] ?? '';

            return `${day.padStart(DATE_PAD_LENGTH, '0')}/${month.padStart(DATE_PAD_LENGTH, '0')}/${year}`;
        }

        const spanishMatch = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/.exec(textValue);

        if (spanishMatch !== null) {
            const day = spanishMatch[1] ?? '';
            const month = spanishMatch[2] ?? '';
            const year = spanishMatch[3] ?? '';

            return `${day.padStart(DATE_PAD_LENGTH, '0')}/${month.padStart(DATE_PAD_LENGTH, '0')}/${year}`;
        }

        return textValue;
    }

    private static serializeCatalogBackendValue<T>(
        value: unknown,
        configuration: ReactiveFieldSerializerDefinition,
        field: FormField<T> | undefined,
        fieldName: string,
        resolvers: ReactiveFormPayloadSerializerResolvers,
    ): unknown {
        const resolvedBackendValue = resolvers.resolveDropdownBackendValue?.(fieldName, value);

        if (this.hasSerializableValue(resolvedBackendValue)) {
            return this.serializeCatalogKeyTextPart(resolvedBackendValue, configuration, field);
        }

        const rawCandidate = this.resolveOptionRawCandidate(value);
        const rawBackendValue = this.extractConfiguredObjectValue(rawCandidate, configuration, field);

        if (this.hasSerializableValue(rawBackendValue)) {
            return this.serializeCatalogKeyTextPart(rawBackendValue, configuration, field);
        }

        const directBackendValue = this.extractConfiguredObjectValue(value, configuration, field);

        if (this.hasSerializableValue(directBackendValue)) {
            return this.serializeCatalogKeyTextPart(directBackendValue, configuration, field);
        }

        return this.serializeCatalogKeyValue(value, configuration, field);
    }

    private static serializeOptionRawValue<T>(
        value: unknown,
        configuration: ReactiveFieldSerializerDefinition,
        field: FormField<T> | undefined,
        fieldName: string,
        resolvers: ReactiveFormPayloadSerializerResolvers,
    ): unknown {
        const resolvedRawValue = resolvers.resolveDropdownRawValue?.(fieldName, value);

        if (this.hasSerializableValue(resolvedRawValue)) {
            const resolvedBackendValue = this.extractConfiguredObjectValue(resolvedRawValue, configuration, field);

            return this.hasSerializableValue(resolvedBackendValue)
                ? resolvedBackendValue
                : resolvedRawValue;
        }

        const rawCandidate = this.resolveOptionRawCandidate(value);
        const rawBackendValue = this.extractConfiguredObjectValue(rawCandidate, configuration, field);

        if (this.hasSerializableValue(rawBackendValue)) {
            return rawBackendValue;
        }

        return rawCandidate === value
            ? this.serializeScalarValue(value, configuration, field)
            : this.serializeScalarValue(rawCandidate, configuration, field);
    }

    private static serializeCatalogKeyValue<T>(
        value: unknown,
        configuration: ReactiveFieldSerializerDefinition,
        field: FormField<T> | undefined,
    ): unknown {
        const scalarValue = this.serializeScalarValue(value, configuration, field);

        return this.serializeCatalogKeyTextPart(scalarValue, configuration, field);
    }

    private static serializeCatalogKeyTextPart<T>(
        value: unknown,
        configuration: ReactiveFieldSerializerDefinition,
        field: FormField<T> | undefined,
    ): unknown {
        const scalarValue = this.serializeScalarValue(value, configuration, field);
        const rawValue = String(scalarValue ?? '').trim();

        if (!rawValue.includes(CATALOG_KEY_SEPARATOR)) {
            return scalarValue;
        }

        const parts = rawValue.split(CATALOG_KEY_SEPARATOR).map((part) => part.trim());
        const selectedPart = this.selectCatalogKeyPart(parts, configuration.catalogKeyPart ?? 'last');

        return selectedPart !== '' ? selectedPart : scalarValue;
    }

    private static selectCatalogKeyPart(
        parts: readonly string[],
        part: ReactiveFieldSerializerDefinition['catalogKeyPart'],
    ): string {
        switch (part) {
            case 'full':
                return parts.join(CATALOG_KEY_SEPARATOR);
            case 'source':
                return parts[0] ?? '';
            case 'primary':
                return parts[1] ?? '';
            case 'secondary':
                return parts[2] ?? '';
            case 'last':
            default:
                return parts.at(-1) ?? '';
        }
    }

    private static resolveOptionRawCandidate(value: unknown): unknown {
        if (value === null || value === undefined || typeof value !== 'object' || value instanceof Date) {
            return value;
        }

        const record = value as Record<string, unknown>;

        return Object.prototype.hasOwnProperty.call(record, 'raw') ? record['raw'] : value;
    }

    private static extractConfiguredObjectValue<T>(
        value: unknown,
        configuration: ReactiveFieldSerializerDefinition,
        field: FormField<T> | undefined,
    ): unknown {
        if (value === null || value === undefined || typeof value !== 'object' || value instanceof Date) {
            return undefined;
        }

        const backendValueKey = configuration.backendValueKey ??
            (field ? ReactiveFormValueUtils.getFieldBackendValueKey(field) : 'id');
        const identityKeys = configuration.identityKeys ??
            (field ? ReactiveFormValueUtils.getFieldRawIdentityKeys(field) : []);
        const keys = ReactiveFormValueUtils.toUniqueIdentityKeys([backendValueKey, ...identityKeys]);
        const record = value as Record<string, unknown>;

        for (const key of keys) {
            const primaryValue = record[key];

            if (this.hasSerializableValue(primaryValue)) {
                return primaryValue;
            }
        }

        return undefined;
    }

    private static serializeScalarValue<T>(
        value: unknown,
        configuration: ReactiveFieldSerializerDefinition,
        field: FormField<T> | undefined,
    ): unknown {
        const backendValueKey = configuration.backendValueKey ??
            (field ? ReactiveFormValueUtils.getFieldBackendValueKey(field) : 'id');
        const identityKeys = configuration.identityKeys ??
            (field ? ReactiveFormValueUtils.getFieldRawIdentityKeys(field) : []);

        return this.extractSerializableValue(value, backendValueKey, identityKeys);
    }

    private static serializeConfiguredRepeatableAggregate(
        rawValue: unknown,
        data: Record<string, unknown>,
        configuration: ReactiveFieldSerializerDefinition,
    ): Record<string, unknown> {
        const rows = this.resolveConfiguredRepeatableRows(rawValue, data, configuration);
        const targets = configuration.repeatableTargets ?? {};
        const separator = configuration.separator ?? ';';
        const shouldTrimTrailingEmptyRows = configuration.repeatableTrimTrailingEmptyRows !== false;
        const payload: Record<string, unknown> = {};

        for (const [logicalKey, target] of Object.entries(targets)) {
            const targetConfigs = this.normalizeConfiguredRepeatableTargets(target);
            const values = rows.map((row) => this.serializeRepeatableCell(row[logicalKey]));
            const trimmedValues = shouldTrimTrailingEmptyRows ? this.trimTrailingEmptyValues(values) : values;

            for (const targetConfig of targetConfigs) {
                payload[targetConfig.fieldName] = trimmedValues
                    .map((value) => this.hasSerializableValue(value)
                        ? value
                        : this.resolveConfiguredTargetEmptyValue(targetConfig.emptyValue, ''))
                    .join(separator);
            }
        }

        return payload;
    }

    private static serializeConfiguredRepeatableSlots(
        rawValue: unknown,
        data: Record<string, unknown>,
        configuration: ReactiveFieldSerializerDefinition,
    ): Record<string, unknown> {
        const rows = this.resolveConfiguredRepeatableRows(rawValue, data, configuration);
        const targets = configuration.repeatableTargets ?? {};
        const rowLimit = configuration.repeatableRows ?? rows.length;
        const payload: Record<string, unknown> = {};

        for (const [logicalKey, target] of Object.entries(targets)) {
            const targetConfigs = this.normalizeConfiguredRepeatableTargets(target);

            for (const targetConfig of targetConfigs) {
                for (let rowIndex = 0; rowIndex < rowLimit; rowIndex += 1) {
                    const targetName = this.formatLegacySlotName(targetConfig.fieldName, rowIndex + 1);
                    const serializedValue = this.serializeRepeatableCell(rows[rowIndex]?.[logicalKey]);

                    payload[targetName] = this.hasSerializableValue(serializedValue)
                        ? serializedValue
                        : this.resolveConfiguredTargetEmptyValue(targetConfig.emptyValue, '');
                }
            }
        }

        return payload;
    }

    private static serializeConfiguredRepeatableJson(
        rawValue: unknown,
        data: Record<string, unknown>,
        configuration: ReactiveFieldSerializerDefinition,
    ): unknown {
        const rows = this.resolveConfiguredRepeatableRows(rawValue, data, configuration);
        const serializedRows = rows.map((row) => this.serializeRepeatableRow(row, configuration));

        return configuration.repeatableJsonString === false ? serializedRows : JSON.stringify(serializedRows);
    }

    private static serializeRepeatableRow(
        row: Record<string, unknown>,
        configuration: ReactiveFieldSerializerDefinition,
    ): Record<string, unknown> {
        const targetKeys = Object.keys(configuration.repeatableTargets ?? {});
        const rowKeys = targetKeys.length > 0 ? targetKeys : Object.keys(row);

        return rowKeys.reduce<Record<string, unknown>>((result, key) => {
            result[key] = this.serializeRepeatableCell(row[key]);

            return result;
        }, {});
    }

    private static resolveConfiguredRepeatableRows(
        rawValue: unknown,
        data: Record<string, unknown>,
        configuration: ReactiveFieldSerializerDefinition,
    ): Array<Record<string, unknown>> {
        if (Array.isArray(rawValue)) {
            return rawValue
                .filter((row): row is Record<string, unknown> => row !== null && typeof row === 'object')
                .map((row) => ({ ...row }));
        }

        return this.resolveRowsFromConfiguredBindings(data, configuration);
    }

    private static resolveRowsFromConfiguredBindings(
        data: Record<string, unknown>,
        configuration: ReactiveFieldSerializerDefinition,
    ): Array<Record<string, unknown>> {
        const bindings = Object.entries(configuration.repeatableBindings ?? {});

        if (!bindings.length) {
            return [];
        }

        const separator = configuration.separator ?? ';';
        const bindingValues = bindings.map(([logicalKey, targetControlName]) => ({
            logicalKey,
            values: this.splitRepeatableBindingValue(data[targetControlName], separator),
        }));
        const rowCount = Math.max(configuration.repeatableRows ?? 0, ...bindingValues.map(({ values }) => values.length));
        const rows = Array.from({ length: rowCount }, () => ({} as Record<string, unknown>));

        for (const { logicalKey, values } of bindingValues) {
            values.forEach((value, rowIndex) => {
                if (rows[rowIndex]) {
                    rows[rowIndex][logicalKey] = value;
                }
            });
        }

        return rows;
    }

    private static splitRepeatableBindingValue(value: unknown, separator: string): unknown[] {
        if (Array.isArray(value)) {
            return value;
        }

        if (!this.hasSerializableValue(value)) {
            return [];
        }

        return String(value).split(separator);
    }

    private static normalizeConfiguredRepeatableTargets(
        target: ReactiveRepeatableSerializerTargetInput | readonly ReactiveRepeatableSerializerTargetInput[],
    ): ReactiveRepeatableSerializerTarget[] {
        const targets = Array.isArray(target) ? target : [target];

        return targets.map((item) => (typeof item === 'string' ? { fieldName: item } : item));
    }

    private static applyRepeatableLegacyAggregateSerializations<T>(
        form: Form<T>,
        data: Record<string, unknown>,
        payload: Record<string, unknown>,
    ): Record<string, unknown> {
        for (const field of this.getRepeatableFields(form)) {
            const configuration = field.input.repeatableGroupConfiguration;
            const aggregateConfiguration = configuration?.legacyAggregateSerialization;

            if (!configuration || !aggregateConfiguration) {
                continue;
            }

            const rows = this.resolveRepeatableRows(field.name, data, configuration);
            const targets = aggregateConfiguration.targets ?? configuration.bindings ?? {};
            const separator = aggregateConfiguration.separator ?? configuration.serialization?.separator ?? ';';
            const shouldTrimTrailingEmptyRows = aggregateConfiguration.trimTrailingEmptyRows !== false;
            const anchoredRows = shouldTrimTrailingEmptyRows
                ? this.trimRepeatableRows(rows, aggregateConfiguration.trimTrailingEmptyRowsBy)
                : rows;

            for (const [logicalKey, target] of Object.entries(targets)) {
                const targetConfigs = this.normalizeLegacyAggregateTargets(target);
                const subField = this.findRepeatableSubField(configuration, logicalKey);
                const values = anchoredRows.map((row) => this.serializeRepeatableCell(row[logicalKey], subField));
                const trimmedValues = shouldTrimTrailingEmptyRows && aggregateConfiguration.trimTrailingEmptyRowsBy === undefined
                    ? this.trimTrailingEmptyValues(values)
                    : values;

                for (const targetConfig of targetConfigs) {
                    payload[targetConfig.fieldName] = trimmedValues
                        .map((value) => this.hasSerializableValue(value)
                            ? value
                            : this.resolveConfiguredTargetEmptyValue(targetConfig.emptyValue, ''))
                        .join(separator);
                }
            }
        }

        return payload;
    }

    private static trimRepeatableRows(
        rows: Array<Record<string, unknown>>,
        anchorKeys: string | readonly string[] | undefined,
    ): Array<Record<string, unknown>> {
        if (anchorKeys === undefined) {
            return rows;
        }

        const keys = Array.isArray(anchorKeys) ? anchorKeys : [anchorKeys];
        let lastNonEmptyIndex = -1;

        rows.forEach((row, rowIndex) => {
            if (keys.some((key) => this.hasSerializableValue(this.serializeRepeatableCell(row[key])))) {
                lastNonEmptyIndex = rowIndex;
            }
        });

        return lastNonEmptyIndex === -1 ? [] : rows.slice(0, lastNonEmptyIndex + 1);
    }

    private static applyRepeatableLegacySlotSerializations<T>(
        form: Form<T>,
        data: Record<string, unknown>,
        payload: Record<string, unknown>,
    ): Record<string, unknown> {
        for (const field of this.getRepeatableFields(form)) {
            const configuration = field.input.repeatableGroupConfiguration;
            const legacyConfiguration = configuration?.legacySlotSerialization;

            if (!configuration || !legacyConfiguration) {
                continue;
            }

            const rows = this.resolveRepeatableRows(field.name, data, configuration);
            const rowLimit = legacyConfiguration.rows ?? configuration.rows ?? rows.length;
            const targets = legacyConfiguration.targets ?? {};

            for (const [logicalKey, target] of Object.entries(targets)) {
                const targetConfigs = this.normalizeLegacyTargets(target);
                const subField = this.findRepeatableSubField(configuration, logicalKey);

                for (const targetConfig of targetConfigs) {
                    for (let rowIndex = 0; rowIndex < rowLimit; rowIndex += 1) {
                        const targetName = this.formatLegacySlotName(targetConfig.fieldName, rowIndex + 1);
                        const rawValue = rows[rowIndex]?.[logicalKey];
                        const serializedValue = this.serializeRepeatableCell(rawValue, subField);

                        payload[targetName] = this.hasSerializableValue(serializedValue)
                            ? serializedValue
                            : this.resolveConfiguredTargetEmptyValue(targetConfig.emptyValue, '');
                    }
                }
            }

            delete payload[field.name];

            if (legacyConfiguration.removeBindingFields === true) {
                Object.values(configuration.bindings ?? {}).forEach((binding) => delete payload[String(binding)]);
            }
        }

        return payload;
    }

    private static getRepeatableFields<T>(form: Form<T>): Array<FormField<T>> {
        const fields: Array<FormField<T>> = [];

        for (const section of form.sections ?? []) {
            for (const field of section.fields ?? []) {
                if (field.input?.type === InputType.REPEATABLE_GROUP) {
                    fields.push(field);
                }
            }
        }

        return fields;
    }

    private static resolveRepeatableRows(
        fieldName: string,
        data: Record<string, unknown>,
        configuration: RepeatableGroupConfiguration,
    ): Array<Record<string, unknown>> {
        const rawRows = data[fieldName];

        if (Array.isArray(rawRows)) {
            return rawRows
                .filter((row): row is Record<string, unknown> => row !== null && typeof row === 'object')
                .map((row) => ({ ...row }));
        }

        return this.resolveRowsFromBindings(data, configuration);
    }

    private static resolveRowsFromBindings(
        data: Record<string, unknown>,
        configuration: RepeatableGroupConfiguration,
    ): Array<Record<string, unknown>> {
        const separator = configuration.serialization?.separator ?? ';';
        const bindings = Object.entries(configuration.bindings ?? {});
        const rowCount = Math.max(
            configuration.rows ?? 0,
            ...bindings.map(([, targetControlName]) => String(data[targetControlName] ?? '').split(separator).length),
        );
        const rows = Array.from({ length: rowCount }, () => ({} as Record<string, unknown>));

        for (const [logicalKey, targetControlName] of bindings) {
            const values = String(data[targetControlName] ?? '').split(separator);

            values.forEach((value, rowIndex) => {
                if (rows[rowIndex]) {
                    rows[rowIndex][logicalKey] = value;
                }
            });
        }

        return rows;
    }


    private static findRepeatableSubField(
        configuration: RepeatableGroupConfiguration,
        logicalKey: string,
    ): RepeatableSubField | undefined {
        return configuration.subFields?.find((subField) => subField.key === logicalKey);
    }

    private static serializeRepeatableCell(value: unknown, subField?: RepeatableSubField): string {
        return RepeatableValueUtils.serializeCellValue(value, subField, false);
    }

    private static normalizeLegacyTargets(
        target: RepeatableLegacySlotTargetInput | RepeatableLegacySlotTargetInput[],
    ): RepeatableLegacySlotTarget[] {
        const targets = Array.isArray(target) ? target : [target];

        return targets.map((item) => (typeof item === 'string' ? { fieldName: item } : item));
    }

    private static normalizeLegacyAggregateTargets(
        target: RepeatableLegacyAggregateTargetInput | RepeatableLegacyAggregateTargetInput[],
    ): RepeatableLegacyAggregateTarget[] {
        const targets = Array.isArray(target) ? target : [target];

        return targets.map((item) => (typeof item === 'string' ? { fieldName: item } : item));
    }

    private static resolveConfiguredTargetEmptyValue(emptyValue: unknown, fallbackValue: string): unknown {
        return emptyValue === undefined ? fallbackValue : emptyValue;
    }

    private static trimTrailingEmptyValues(values: unknown[]): unknown[] {
        let lastNonEmptyIndex = values.length - 1;

        while (lastNonEmptyIndex >= 0 && !this.hasSerializableValue(values[lastNonEmptyIndex])) {
            lastNonEmptyIndex -= 1;
        }

        return values.slice(0, lastNonEmptyIndex + 1);
    }

    private static formatLegacySlotName(template: string, oneBasedIndex: number): string {
        return template
            .replaceAll(SLOT_INDEX_PLACEHOLDER, String(oneBasedIndex))
            .replaceAll(SLOT_NUMBER_PLACEHOLDER, String(oneBasedIndex));
    }

    private static hasSerializableValue(value: unknown): boolean {
        return value !== null && value !== undefined && String(value).trim() !== '';
    }

    private static extractSerializableValue(
        value: unknown,
        primaryKey: string,
        identityKeys: readonly string[] = [],
    ): unknown {
        if (value === null || value === undefined) {
            return value;
        }

        if (typeof value === 'object' && !(value instanceof Date)) {
            const record = value as Record<string, unknown>;
            const keys = ReactiveFormValueUtils.toUniqueIdentityKeys([primaryKey, ...identityKeys]);

            for (const key of keys) {
                const primaryValue = record[key];

                if (primaryValue !== null && primaryValue !== undefined) {
                    return primaryValue;
                }
            }
        }

        return value;
    }

    private static isDateField<T>(field: FormField<T> | undefined): boolean {
        return field?.input.type === InputType.CALENDAR;
    }

    private static resolveDateFieldFormat<T>(field: FormField<T> | undefined): ReactiveFieldSerializerDefinition['dateFormat'] {
        const dateFormat = field?.input.calendarConfiguration?.calendarDateFormat?.toLowerCase() ?? '';

        return dateFormat.startsWith('dd') ? 'spanish' : 'iso';
    }

    private static isTimeField<T>(field: FormField<T> | undefined, fieldName: string): boolean {
        return field?.input.type === InputType.TIME || fieldName.toLowerCase().includes('hora');
    }

    private static formatTimeValue(value: Date): string {
        const hours = value.getHours().toString().padStart(TIME_PAD_LENGTH, '0');
        const minutes = value.getMinutes().toString().padStart(TIME_PAD_LENGTH, '0');

        return `${hours}:${minutes}`;
    }
}

export class ReactiveFormValidationMessageBuilder {
    private static readonly ERROR_MESSAGES: Record<string, (errors: Record<string, string>) => string> = {
        required: () => 'es obligatorio.',
        maxlength: () => 'ha excedido la longitud máxima.',
        email: () => 'no es un correo válido.',
        invalidTime: (errors) => errors['invalidTime'] ?? '',
        invalidSpanishId: (errors) => errors['invalidSpanishId'] ?? '',
        spanishIdTaken: (errors) => errors['spanishIdTaken'] ?? '',
        duplicateValue: (errors) => errors['duplicateValue'] ?? '',
    };

    public static build<T>(form: Form<T>, model: FormGroup): string {
        const formOperations = new FormOperations(form, model);
        const controls = model.controls as Record<string, { invalid: boolean; errors: Record<string, string> | null }>;
        const invalidFields = Object.entries(controls).reduce<string[]>((messages, [controlName, control]) => {
            if (!control.invalid) {
                return messages;
            }

            const fieldInfo = formOperations.getFieldByControlName(controlName);
            const errors = control.errors ?? {};
            const message = Object.keys(errors)
                .filter((key) => key in this.ERROR_MESSAGES)
                .map((key) => this.ERROR_MESSAGES[key]?.(errors as Record<string, string>))
                .join(' ');

            messages.push(`${fieldInfo?.label ?? controlName} ${message || ': Error desconocido.'}`);

            return messages;
        }, []);

        return `El formulario no es válido. Revisa los campos:<br>- ${invalidFields.join('<br>- ')}`;
    }
}
